import {isLinkPrefetchingEnabled} from '@/utils/env';
import {documentToReactComponents} from '@contentful/rich-text-react-renderer';
import {BLOCKS, INLINES, MARKS} from '@contentful/rich-text-types';
import OpenInNew from '@mui/icons-material/OpenInNew';
import {Box} from '@mui/system';
import Image from 'next/image';
import Link from 'next/link';
import styles from './MarkDown.module.scss';

let fontSize: any;
const options: any = {
  renderMark: {
    [MARKS.CODE]: (text: string) => (
      <pre>
        <code>{text}</code>
      </pre>
    ),
  },
  renderNode: {
    [BLOCKS.PARAGRAPH]: (node: any, children: any) => {
      if (
        node.content.find((item: any) =>
          item?.marks?.find((mark: any) => mark.type === 'code'),
        )
      ) {
        return (
          <div>
            <pre>
              <code>{children}</code>
            </pre>
          </div>
        );
      }
      return fontSize !== 'undefined' ? (
        <span
          style={{
            fontSize: fontSize,
          }}
        >
          {children}
        </span>
      ) : (
        <span>{children}</span>
      );
    },

    [INLINES.ENTRY_HYPERLINK]: (node: any) => (
      <Link href={`/${node?.data?.target?.fields?.slug}`}>
        {node?.data?.target?.fields?.title}
        prefetch={isLinkPrefetchingEnabled()}
      </Link>
    ),
    [BLOCKS.UL_LIST]: (node: any, children: any) => <ul>{children}</ul>,
    [BLOCKS.OL_LIST]: (node: any, children: any) => <ol>{children}</ol>,
    [BLOCKS.LIST_ITEM]: (node: any, children: any) => <li>{children}</li>,
    [BLOCKS.HEADING_1]: (node: any, children: any) =>
      fontSize !== 'undefined' ? (
        <h1 style={{fontSize: fontSize}}>{children}</h1>
      ) : (
        <h1>{children}</h1>
      ),
    [BLOCKS.HEADING_2]: (node: any, children: any) =>
      fontSize !== 'undefined' ? (
        <h2 style={{fontSize: fontSize}}>{children}</h2>
      ) : (
        <h2>{children}</h2>
      ),
    [BLOCKS.HEADING_3]: (node: any, children: any) => {
      const id =
        children && children[0] && children[0].length > 50
          ? ''
          : children[0] && children[0].length > 0
            ? children[0]
                .substring(0, 50)
                .toLowerCase()
                .replace(/ /g, '-')
                .replace(/[^a-zA-Z0-9-.?@&]/g, '')
            : '';

      return fontSize !== 'undefined' ? (
        <h3
          style={{fontSize: fontSize}}
          id={id}
        >
          {children}
        </h3>
      ) : (
        <h3 id={id}>{children}</h3>
      );
    },
    [BLOCKS.HEADING_4]: (node: any, children: any) => {
      const id =
        children && children[0] && children[0].length > 50
          ? ''
          : children[0] && children[0].length > 0
            ? children[0]
                .substring(0, 50)
                .toLowerCase()
                .replace(/ /g, '-')
                .replace(/[^a-zA-Z0-9-.?@&]/g, '')
            : '';

      return fontSize !== 'undefined' ? (
        <h4
          style={{fontSize: fontSize}}
          id={id}
        >
          {children}
        </h4>
      ) : (
        <h4 id={id}>{children}</h4>
      );
    },
    [BLOCKS.HEADING_5]: (node: any, children: any) =>
      fontSize !== 'undefined' ? (
        <h5 style={{fontSize: fontSize}}>{children}</h5>
      ) : (
        <h5>{children}</h5>
      ),
    [BLOCKS.HEADING_6]: (node: any, children: any) =>
      fontSize !== 'undefined' ? (
        <h6 style={{fontSize: fontSize}}>{children}</h6>
      ) : (
        <h6>{children}</h6>
      ),
    [INLINES.HYPERLINK]: (node: any) => {
      const text = node?.content?.find(
        (item: any) => item?.nodeType === 'text',
      )?.value;
      return (
        <Link
          href={`${node?.data?.uri}`}
          className={'link-text'}
          target={
            node?.data?.uri.includes('gfiber.com') ||
            node?.data?.uri.includes('fiber.google.com') ||
            node?.data?.uri?.startsWith('/') ||
            node?.data?.uri?.includes('tel')
              ? ''
              : '_blank'
          }
          rel="noopener noreferrer"
          prefetch={isLinkPrefetchingEnabled()}
        >
          {text}
        </Link>
      );
    },

    [INLINES.EMBEDDED_ENTRY]: (node: any) => {
      if (
        node?.data?.target?.sys?.contentType?.sys?.id ===
        'fragmentMultiColorText'
      ) {
        fontSize = `${node?.data?.target?.fields?.gfFontSize}`;
        return (
          <span
            style={{
              color: `${node?.data?.target?.fields?.gfColor}`,
            }}
          >
            {documentToReactComponents(
              node?.data?.target?.fields?.gfText,
              options,
            )}
          </span>
        );
      }

      return (
        <Box
          className={`text-[${node?.data?.target?.fields?.gfCtaColor}] hover:text-[${node?.data?.target?.fields?.gfCtaHoverColor}]`}
          component="span"
        >
          <a
            href={
              node?.data?.target?.fields?.gfCtaLink
                ? node?.data?.target?.fields?.gfCtaLink
                : undefined
            }
            target={node?.data?.target?.fields?.gfExternalLink ? '_blank' : ''}
          >
            <span>
              {documentToReactComponents(
                node?.data?.target?.fields?.gfCtaText,
                options,
              )}
            </span>
            {node?.data?.target?.fields?.gfExternalLink ? (
              <OpenInNew className={styles.openInNew} />
            ) : (
              ''
            )}
          </a>
        </Box>
      );
    },

    [BLOCKS.EMBEDDED_ASSET]: (node: any) => (
      <Image
        src={`${node?.data?.target?.fields?.file?.url}`}
        height={node?.data?.target?.fields?.file?.details?.image?.height}
        width={node?.data?.target?.fields?.file?.details?.image?.width}
        alt={node?.data?.target?.fields?.title}
      />
    ),
    [BLOCKS.TABLE]: (node: any, children: any) => (
      <table>
        <tbody>{children}</tbody>
      </table>
    ),
    [BLOCKS.TABLE_ROW]: (node: any, children: any) => <tr>{children}</tr>,
    [BLOCKS.TABLE_CELL]: (node: any, children: any) => <td>{children}</td>,
  },
};

const MarkDown = ({content}: any) => (
  <>{documentToReactComponents(content, options)}</>
);

export default MarkDown;
